import { useMemo } from "react";
// import { Zap } from "lucide-react";
import { calculateETA, formatPercentage, calculateMetricsFromTasks } from "@/lib/utils";
import type { DashboardState } from "@/types/dashboard";

interface ProgressBarProps {
  state: DashboardState;
}

export function ProgressBar({ state }: ProgressBarProps) {
  // Calculate metrics from tasks (single source of truth)
  const metrics = useMemo(() => {
    if (state.tasks && Object.keys(state.tasks).length > 0) {
      return calculateMetricsFromTasks(state.tasks, state.total_questions);
    }
    return state.metrics;
  }, [state.tasks, state.total_questions, state.metrics]);

  const { completed, total, percentage, eta, isCompleted } = useMemo(() => {
    const successful = metrics?.completed || 0;
    const failed = metrics?.failed || 0;
    const completed = successful + failed;
    const total = state.total_questions || 1;
    const percentage = Math.min(100, (completed / total) * 100);
    const running = metrics?.running || 0;
    const queued = metrics?.queued || 0;

    // Check if actually completed (no running/queued tasks)
    const isCompleted = running === 0 && queued === 0 && completed >= total;

    let eta = "Calculating...";
    if (!isCompleted && state.started_at && completed > 0) {
      const elapsedMs = Date.now() - new Date(state.started_at).getTime();
      eta = calculateETA(completed, total, elapsedMs);
    } else if (isCompleted) {
      eta = "Completed";
    }

    return { completed, total, percentage, eta, isCompleted };
  }, [metrics, state.total_questions, state.started_at]);

  // const isRunning = useMemo(() => {
  //   if (!state.metrics) return false;
  //   // Check view mode - if historical, it's not running
  //   if (state.viewMode === 'historical') return false;
  //   return state.metrics.running > 0 || state.metrics.queued > 0;
  // }, [state.metrics, state.viewMode]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-2xl font-bold text-primary">
            {completed} <span className="text-muted-foreground">/ {total}</span>
          </span>
          <span className="text-sm text-muted-foreground">
            {formatPercentage(percentage, 0)} complete
          </span>
        </div>
        {!isCompleted && (
          <div className="text-sm">
            <span className="text-muted-foreground">ETA: </span>
            <span className="font-medium text-warning">{eta}</span>
          </div>
        )}
        {isCompleted && (
          <div className="flex items-center gap-2 text-sm">
            <div className="h-2 w-2 rounded-full bg-success" />
            <span className="font-medium text-success">Completed</span>
          </div>
        )}
      </div>

      <div className="relative h-6 w-full overflow-hidden rounded-full bg-secondary border border-primary/20">
        {isCompleted ? (
          <>
            <div
              className="absolute h-full bg-gradient-to-r from-success via-emerald-500 to-success transition-all duration-700 ease-out"
              style={{ width: `${((metrics?.completed || 0) / total) * 100}%` }}
            />
            <div
              className="absolute h-full bg-gradient-to-r from-destructive via-red-600 to-destructive transition-all duration-700 ease-out"
              style={{
                left: `${((metrics?.completed || 0) / total) * 100}%`,
                width: `${((metrics?.failed || 0) / total) * 100}%`
              }}
            />
          </>
        ) : (
          <div
            className="h-full transition-all duration-700 ease-out bg-gradient-to-r from-primary via-orange-500 to-primary animate-gradient glow-strong"
            style={{ width: `${percentage}%` }}
          />
        )}
      </div>

      {metrics && (
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-warning animate-pulse" />
            <span className="text-muted-foreground">Running:</span>
            <span className="font-bold text-warning">{metrics.running}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-blue-500" />
            <span className="text-muted-foreground">Queued:</span>
            <span className="font-bold text-blue-400">{metrics.queued}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-destructive" />
            <span className="text-muted-foreground">Failed:</span>
            <span className="font-bold text-destructive">{metrics.failed}</span>
          </div>
        </div>
      )}
    </div>
  );
}
